import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from "firebase/auth";
import { auth } from "../config/firebase";

// Popup is the primary flow. signInWithRedirect alone breaks on Safari (ITP),
// Firefox and Chrome 115+ whenever authDomain (orucom1.firebaseapp.com) is a
// different site than the app: the redirect result is stored in partitioned
// third-party storage and getRedirectResult() comes back null.
// Redirect is only used when the browser can't open a popup.
const REDIRECT_FALLBACK_CODES = new Set([
  "auth/popup-blocked",
  "auth/operation-not-supported-in-this-environment",
]);

// Errors that just mean the user backed out — don't show an error dialog.
const USER_CANCELLED_CODES = new Set([
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/user-cancelled",
]);

const createProvider = () => {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  return provider;
};

// Resolves with the Firebase ID token, or null when the page is navigating
// away for the redirect fallback (the result is then picked up on return by
// getGoogleRedirectIdToken).
// Must be called directly from a click handler, before any other await, so
// Safari treats the popup as user-initiated.
export const signInWithGoogle = async () => {
  const provider = createProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    return await result.user.getIdToken();
  } catch (err) {
    if (REDIRECT_FALLBACK_CODES.has(err?.code)) {
      await signInWithRedirect(auth, provider);
      return null;
    }
    throw err;
  }
};

// Returns the ID token if we just came back from the redirect fallback.
export const getGoogleRedirectIdToken = async () => {
  const result = await getRedirectResult(auth);
  return result ? result.user.getIdToken() : null;
};

export const isGoogleSignInCancelled = (err) =>
  USER_CANCELLED_CODES.has(err?.code);
